import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    ArrowLeft, Plus, Rss, Trash2, ExternalLink, PlayCircle, XCircle,
    Image as ImageIcon, Calendar, AlertCircle, CheckCircle2, Facebook, Sparkles,
    RefreshCw,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import {
    getPageConfig, listFeedsForPage, setFeedsForPage,
    listRssFeeds, listPosts, publishPostNow, cancelPost,
    regeneratePost, generateVideoForPost, publishVideoNow,
    enhancePostImage,
    type SocialPost, type SocialRssFeed,
} from '@/services/api/social-publisher';
import { getCurrentUser } from '@/services/api/auth';
import { Film } from 'lucide-react';
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { fmtDateTime, fmtRelative } from './dates';
import SyncModal from './components/SyncModal';
import RescheduleModal from './components/RescheduleModal';


function LinkFeedsDialog({
    open, onOpenChange, pageConfigId, currentFeedIds, onSaved,
}: {
    open: boolean; onOpenChange: (b: boolean) => void;
    pageConfigId: number; currentFeedIds: number[];
    onSaved: () => void;
}) {
    const { toast } = useToast();
    const [selected, setSelected] = useState<Set<number>>(new Set(currentFeedIds));
    const { data: feeds = [] } = useQuery({
        queryKey: ['social-publisher', 'rss-feeds'],
        queryFn: listRssFeeds,
        enabled: open,
    });

    const mut = useMutation({
        mutationFn: () => setFeedsForPage(pageConfigId, Array.from(selected)),
        onSuccess: () => { toast({ title: 'Vinculação atualizada' }); onSaved(); },
        onError: (e: any) => toast({
            title: 'Erro',
            description: e?.response?.data?.detail || 'Falha',
            variant: 'destructive',
        }),
    });

    const toggle = (id: number) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelected(next);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px] p-0 border-none rounded-2xl overflow-hidden">
                <div className="bg-white">
                    <div className="px-5 py-3 border-b">
                        <h3 className="text-base font-semibold">Vincular feeds RSS</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Selecione quais feeds vão alimentar esta página
                        </p>
                    </div>
                    <div className="p-5 max-h-[60vh] overflow-y-auto">
                        {feeds.length === 0 ? (
                            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-3 flex items-start gap-2">
                                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                                <span>
                                    Nenhum feed cadastrado. Crie em{' '}
                                    <Link to="/meta/social-publisher/feeds" className="underline">
                                        Feeds RSS
                                    </Link>.
                                </span>
                            </div>
                        ) : (
                            <div className="space-y-1.5">
                                {feeds.map((f: SocialRssFeed) => (
                                    <label
                                        key={f.id}
                                        className="flex items-start gap-3 px-3 py-2 rounded-lg border hover:bg-gray-50 cursor-pointer"
                                    >
                                        <Checkbox
                                            checked={selected.has(f.id)}
                                            onCheckedChange={() => toggle(f.id)}
                                            className="mt-0.5"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium text-gray-800 truncate">
                                                {f.name || f.feed_url}
                                            </div>
                                            <div className="text-[11px] text-gray-400 truncate">
                                                {f.feed_url}
                                            </div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="px-5 py-3 border-t bg-gray-50/50 flex justify-end gap-2">
                        <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
                        <Button
                            onClick={() => mut.mutate()}
                            disabled={mut.isPending}
                            className="bg-purple-600 hover:bg-purple-700"
                        >
                            {mut.isPending ? 'Salvando...' : 'Salvar vínculos'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}


function PostsTab({ pageConfigId }: { pageConfigId: number }) {
    const qc = useQueryClient();
    const { toast } = useToast();
    const [type, setType] = useState<string>('');
    const [section, setSection] = useState<'free' | 'scheduled' | 'history'>('free');
    // Estado de regen — declarado ANTES do useQuery pra usar no refetchInterval.
    const [regenAt, setRegenAt] = useState<Record<number, number>>({});
    const hasActiveRegen = Object.keys(regenAt).length > 0;
    const { data: posts = [], isLoading } = useQuery({
        queryKey: ['social-publisher', 'posts', pageConfigId, type],
        queryFn: () => listPosts({
            page_config_id: pageConfigId,
            type: type || undefined,
            limit: 200,
        }),
        // Polling agressivo quando tem regen ativa pra pegar update rápido
        refetchInterval: hasActiveRegen ? 3000 : 1000 * 30,
    });

    const isFreeToSchedule = (p: SocialPost) => {
        // 'manual_review' OU sem horário (placeholder 9999)
        return p.status === 'manual_review'
            || (p.scheduled_at?.indexOf('9999') ?? -1) >= 0;
    };
    const isScheduled = (p: SocialPost) =>
        (p.status === 'queued' || p.status === 'sending')
        && (p.scheduled_at?.indexOf('9999') ?? -1) < 0;
    const isHistory = (p: SocialPost) =>
        p.status === 'published' || p.status === 'failed';

    const freePosts = posts.filter(isFreeToSchedule);
    const scheduledPosts = posts.filter(isScheduled).sort((a, b) => {
        const ad = new Date(a.scheduled_at).getTime();
        const bd = new Date(b.scheduled_at).getTime();
        return ad - bd;
    });
    const historyPosts = posts.filter(isHistory).sort((a, b) => {
        const ad = new Date(a.completed_at || a.updated_at || a.created_at || 0).getTime();
        const bd = new Date(b.completed_at || b.updated_at || b.created_at || 0).getTime();
        return bd - ad;
    });

    const currentList =
        section === 'free' ? freePosts
        : section === 'scheduled' ? scheduledPosts
        : historyPosts;

    // Paginação — mostra últimos N; botão "Carregar mais" incrementa.
    // Reseta quando muda seção ou tipo.
    const PAGE_SIZE = 12;
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    useEffect(() => { setVisibleCount(PAGE_SIZE); }, [section, type]);
    const visibleList = currentList.slice(0, visibleCount);
    const hasMore = currentList.length > visibleCount;

    const pub = useMutation({
        mutationFn: (id: number) => publishPostNow(id),
        onSuccess: () => {
            toast({ title: 'Enfileirado pra publicar' });
            qc.invalidateQueries({ queryKey: ['social-publisher', 'posts'] });
        },
    });
    const can = useMutation({
        mutationFn: (id: number) => cancelPost(id),
        onSuccess: () => {
            toast({ title: 'Cancelado' });
            qc.invalidateQueries({ queryKey: ['social-publisher', 'posts'] });
        },
    });

    // Rastreia timestamps de quando cada post começou a regerar.
    // Enquanto post.updated_at (do backend) for < timestamp local, mostra loader.
    const isRegenerating = (p: SocialPost) => {
        const startedAt = regenAt[p.id];
        if (!startedAt) return false;
        const backendUpdated = p.updated_at ? new Date(p.updated_at).getTime() : 0;
        // Se backend já atualizou depois do start, considera pronto e limpa
        if (backendUpdated > startedAt) return false;
        // Timeout de segurança: 3 min (caso falhe silenciosamente)
        if (Date.now() - startedAt > 3 * 60 * 1000) return false;
        return true;
    };

    // Limpa entries de regen já concluídas
    useEffect(() => {
        const stale: number[] = [];
        for (const [idStr, startedAt] of Object.entries(regenAt)) {
            const id = Number(idStr);
            const p = posts.find((x) => x.id === id);
            if (!p) continue;
            const backendUpdated = p.updated_at ? new Date(p.updated_at).getTime() : 0;
            if (backendUpdated > startedAt || Date.now() - startedAt > 3 * 60 * 1000) {
                stale.push(id);
            }
        }
        if (stale.length) {
            setRegenAt((prev) => {
                const next = { ...prev };
                for (const id of stale) delete next[id];
                return next;
            });
        }
    }, [posts, regenAt]);

    const regen = useMutation({
        mutationFn: (args: { id: number; target: 'text' | 'image' | 'both' }) =>
            regeneratePost(args.id, args.target),
        onMutate: (args) => {
            setRegenAt((prev) => ({ ...prev, [args.id]: Date.now() }));
        },
        onSuccess: (_, args) => {
            toast({
                title: 'Regeneração enfileirada',
                description: `Alvo: ${args.target}. Vai levar ~10-30s.`,
            });
        },
        onError: (e: any, args) => {
            setRegenAt((prev) => {
                const next = { ...prev };
                delete next[args.id];
                return next;
            });
            toast({
                title: 'Erro',
                description: e?.response?.data?.detail || 'Falha',
                variant: 'destructive',
            });
        },
    });

    // Modal de reagendar — abre com um post específico
    const [rescheduleTarget, setRescheduleTarget] = useState<SocialPost | null>(null);

    // Admin-only: geração de vídeo
    const currentUser = getCurrentUser();
    const isAdmin = !!currentUser?.owner
        || ['super_admin', 'admin'].includes((currentUser?.user_type || '').toLowerCase());

    const [videoLoading, setVideoLoading] = useState<Record<number, boolean>>({});
    const enhance = useMutation({
        mutationFn: (postId: number) => enhancePostImage(postId),
        onMutate: (postId) => {
            setRegenAt((prev) => ({ ...prev, [postId]: Date.now() }));
        },
        onSuccess: (_, postId) => {
            toast({
                title: 'Embelezar enfileirado',
                description: 'Leva ~30-60s. A foto atualiza sozinha.',
            });
            const start = Date.now();
            const iv = setInterval(async () => {
                const fresh = await listPosts({ page_config_id: pageConfigId, limit: 500 });
                const p = fresh.find((x) => x.id === postId);
                const original = posts.find((x) => x.id === postId);
                if ((p?.ai_image_url && p.ai_image_url !== original?.ai_image_url)
                    || Date.now() - start > 3 * 60_000) {
                    clearInterval(iv);
                    setRegenAt((prev) => {
                        const next = { ...prev };
                        delete next[postId];
                        return next;
                    });
                    qc.invalidateQueries({ queryKey: ['social-publisher', 'posts', pageConfigId] });
                }
            }, 5_000);
        },
        onError: (e: any, postId) => {
            setRegenAt((prev) => {
                const next = { ...prev };
                delete next[postId];
                return next;
            });
            toast({
                title: 'Erro',
                description: e?.response?.data?.detail || 'Falha',
                variant: 'destructive',
            });
        },
    });

    const pubVideo = useMutation({
        mutationFn: (postId: number) => publishVideoNow(postId),
        onSuccess: (_, postId) => {
            toast({
                title: 'Publicação de vídeo enfileirada',
                description: `Post #${postId} vai ser publicado como vídeo no FB.`,
            });
            setTimeout(() => qc.invalidateQueries({ queryKey: ['social-publisher', 'posts', pageConfigId] }), 3000);
        },
        onError: (e: any) => {
            toast({
                title: 'Erro',
                description: e?.response?.data?.detail || 'Falha',
                variant: 'destructive',
            });
        },
    });

    const genVideo = useMutation({
        mutationFn: (postId: number) => generateVideoForPost(postId),
        onMutate: (postId) => {
            setVideoLoading((prev) => ({ ...prev, [postId]: true }));
        },
        onSuccess: (_, postId) => {
            toast({
                title: 'Vídeo enfileirado',
                description: 'Leva ~2-4 min. O vídeo aparece no card quando pronto.',
            });
            // Poll até aparecer video_url
            const start = Date.now();
            const iv = setInterval(async () => {
                const fresh = await listPosts({ page_config_id: pageConfigId, limit: 500 });
                const p = fresh.find((x) => x.id === postId);
                if (p?.ai_video_url || Date.now() - start > 6 * 60_000) {
                    clearInterval(iv);
                    setVideoLoading((prev) => {
                        const next = { ...prev };
                        delete next[postId];
                        return next;
                    });
                    qc.invalidateQueries({ queryKey: ['social-publisher', 'posts', pageConfigId] });
                }
            }, 10_000);
        },
        onError: (e: any, postId) => {
            setVideoLoading((prev) => {
                const next = { ...prev };
                delete next[postId];
                return next;
            });
            toast({
                title: 'Erro',
                description: e?.response?.data?.detail || 'Falha',
                variant: 'destructive',
            });
        },
    });

    const statusBadge = (s: string) => {
        const m: Record<string, string> = {
            queued: 'bg-blue-100 text-blue-700',
            sending: 'bg-amber-100 text-amber-700',
            published: 'bg-emerald-100 text-emerald-700',
            failed: 'bg-red-100 text-red-700',
            manual_review: 'bg-gray-100 text-gray-700',
        };
        return <span className={`text-[10px] px-2 py-0.5 rounded-full ${m[s] || m.manual_review}`}>{s}</span>;
    };

    const sectionMeta = [
        { key: 'free' as const, label: 'Livre pra agendar', count: freePosts.length, color: 'gray' },
        { key: 'scheduled' as const, label: 'Agendados', count: scheduledPosts.length, color: 'blue' },
        { key: 'history' as const, label: 'Histórico', count: historyPosts.length, color: 'emerald' },
    ];

    return (
        <div className="space-y-4">
            {/* Tabs de seção (livre / agendado / histórico) */}
            <div className="flex items-center gap-2 border-b">
                {sectionMeta.map((s) => {
                    const active = section === s.key;
                    const colorActive = {
                        gray: 'border-gray-700 text-gray-800',
                        blue: 'border-blue-600 text-blue-700',
                        emerald: 'border-emerald-600 text-emerald-700',
                    }[s.color];
                    return (
                        <button
                            key={s.key}
                            onClick={() => setSection(s.key)}
                            className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
                                active
                                    ? colorActive
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            {s.label}
                            <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full ${
                                active ? 'bg-gray-100' : 'bg-gray-50 text-gray-400'
                            }`}>
                                {s.count}
                            </span>
                        </button>
                    );
                })}
                <div className="ml-auto flex items-center gap-2">
                    <Label className="text-xs text-gray-500">Tipo:</Label>
                    <select
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        className="h-8 text-xs border rounded px-2"
                    >
                        <option value="">Todos</option>
                        <option value="feed">Feed</option>
                        <option value="story">Story</option>
                    </select>
                </div>
            </div>

            {/* Texto explicativo por seção */}
            <div className="text-xs text-gray-500 -mt-2 space-y-1">
                {section === 'free' && (
                    <>
                        <div>
                            <strong>Fila interna</strong> — posts gerados sem horário. O scheduler
                            puxa da fila e agenda nos slots livres do seu horizonte
                            (padrão 7 dias). Excesso fica aqui aguardando slots vagarem.
                        </div>
                        {freePosts.some((p) => !p.ai_image_url) && (
                            <div className="text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">
                                ⚠ Alguns posts estão sem imagem (gen falhou). Eles NÃO
                                são agendados — cancele ou regenere.
                            </div>
                        )}
                    </>
                )}
                {section === 'scheduled' && (
                    <>Posts com horário cravado — vão pro Facebook automaticamente na hora marcada.</>
                )}
                {section === 'history' && (
                    <>Posts já enviados pro Facebook (sucesso ou falha).</>
                )}
            </div>

            {isLoading ? (
                <div className="text-center text-gray-400 py-8">Carregando...</div>
            ) : currentList.length === 0 ? (
                <div className="bg-white border rounded-xl p-12 text-center text-sm text-gray-500">
                    {section === 'free' && 'Nenhum post livre — gera quando há items RSS novos.'}
                    {section === 'scheduled' && 'Nenhum post agendado ainda.'}
                    {section === 'history' && 'Nenhum post publicado ainda.'}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {visibleList.map((p: SocialPost) => (
                        <div key={p.id} className="bg-white rounded-xl border overflow-hidden">
                            {p.ai_video_url ? (
                                <div className="relative bg-black">
                                    <video
                                        src={p.ai_video_url}
                                        controls
                                        loop
                                        className={
                                            p.type === 'story'
                                                ? 'w-full max-h-[500px] object-contain mx-auto bg-black'
                                                : 'w-full max-h-[420px] object-contain mx-auto bg-black'
                                        }
                                    />
                                    <span className="absolute top-2 left-2 text-[10px] px-1.5 py-0.5 rounded bg-fuchsia-600 text-white font-medium inline-flex items-center gap-1">
                                        <Film className="w-3 h-3" /> VÍDEO (admin)
                                    </span>
                                </div>
                            ) : p.ai_image_url ? (
                                <div className="relative bg-gray-50">
                                    <a
                                        href={p.ai_image_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="block"
                                        title="Abrir imagem em tamanho real"
                                    >
                                        <img
                                            src={p.ai_image_url}
                                            alt={`post-${p.id}`}
                                            className={
                                                (p.type === 'story'
                                                    ? 'w-full max-h-[500px] object-contain mx-auto'
                                                    : 'w-full max-h-[420px] object-contain mx-auto')
                                                + (isRegenerating(p) || videoLoading[p.id] ? ' blur-sm brightness-75 transition-all' : '')
                                            }
                                            loading="lazy"
                                        />
                                    </a>
                                    {(isRegenerating(p) || videoLoading[p.id]) && (
                                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                            <div className="bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg">
                                                {videoLoading[p.id]
                                                    ? <Film className="w-6 h-6 text-fuchsia-600 animate-pulse" />
                                                    : <RefreshCw className="w-6 h-6 text-purple-600 animate-spin" />}
                                            </div>
                                            <div className="mt-3 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 text-xs text-purple-700 font-medium shadow">
                                                {videoLoading[p.id] ? 'Gerando vídeo... (2-4 min)' : 'Regerando...'}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="w-full h-32 bg-gray-50 flex items-center justify-center text-gray-300">
                                    <ImageIcon className="w-8 h-8" />
                                </div>
                            )}
                            <div className="p-3 space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-[11px] text-gray-400">
                                        #{p.id} · {p.type}
                                    </span>
                                    {statusBadge(p.status)}
                                </div>
                                <div className={`text-xs text-gray-700 whitespace-pre-wrap line-clamp-4 ${isRegenerating(p) ? 'opacity-40' : ''}`}>
                                    {p.ai_text || <em className="text-gray-400">sem texto</em>}
                                </div>
                                <div className="text-[10px] text-gray-400 flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {fmtDateTime(p.scheduled_at)}
                                    {p.fb_post_id && (
                                        <a
                                            href={p.fb_post_url || '#'}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="ml-2 text-purple-600 inline-flex items-center gap-0.5"
                                        >
                                            ver no FB <ExternalLink className="w-2.5 h-2.5" />
                                        </a>
                                    )}
                                </div>
                                {p.error_message && (
                                    <div className="text-[10px] text-red-600 flex items-center gap-1">
                                        <AlertCircle className="w-3 h-3" /> {p.error_message}
                                    </div>
                                )}
                                <div className="flex items-center flex-wrap gap-2 pt-2 border-t">
                                    {p.status !== 'published' && p.status !== 'sending' && (
                                        <Button
                                            size="sm" variant="outline"
                                            onClick={() => pub.mutate(p.id)}
                                            disabled={pub.isPending}
                                            className="text-purple-700 border-purple-200 hover:bg-purple-50"
                                        >
                                            <PlayCircle className="w-3 h-3 mr-1" /> Publicar agora
                                        </Button>
                                    )}

                                    {/* Regenerar (dropdown com opções) */}
                                    {p.status !== 'published' && p.status !== 'sending' && (
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button
                                                    size="sm" variant="ghost"
                                                    disabled={regen.isPending}
                                                    className="text-blue-700 hover:bg-blue-50"
                                                >
                                                    <RefreshCw className={`w-3 h-3 mr-1 ${regen.isPending ? 'animate-spin' : ''}`} />
                                                    Regerar
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem
                                                    onClick={() => regen.mutate({ id: p.id, target: 'text' })}
                                                >
                                                    ✏️ Só texto
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    onClick={() => regen.mutate({ id: p.id, target: 'image' })}
                                                >
                                                    🖼️ Só imagem
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    onClick={() => regen.mutate({ id: p.id, target: 'both' })}
                                                >
                                                    🔁 Texto + imagem
                                                </DropdownMenuItem>
                                                {isAdmin && p.ai_image_url && (
                                                    <DropdownMenuItem
                                                        onClick={() => enhance.mutate(p.id)}
                                                        disabled={enhance.isPending}
                                                        className="text-emerald-700 font-medium"
                                                    >
                                                        ✨ Embelezar (admin · ~$0.05)
                                                    </DropdownMenuItem>
                                                )}
                                                {isAdmin && p.ai_image_url && (
                                                    <DropdownMenuItem
                                                        onClick={() => genVideo.mutate(p.id)}
                                                        disabled={!!videoLoading[p.id]}
                                                        className="text-fuchsia-700"
                                                    >
                                                        🎬 Gerar vídeo (admin · ~$0.50)
                                                    </DropdownMenuItem>
                                                )}
                                                {/* Talking video escondido por ora — sadtalker plástico demais.
                                                    Backend segue rodando via /posts/:id/generate-talking-video */}
                                                {isAdmin && p.ai_video_url && p.status !== 'published' && (
                                                    <DropdownMenuItem
                                                        onClick={() => {
                                                            if (confirm('Publicar como vídeo no Facebook agora? (admin-only)')) {
                                                                pubVideo.mutate(p.id);
                                                            }
                                                        }}
                                                        className="text-emerald-700 font-medium"
                                                    >
                                                        📤 Publicar como VÍDEO (admin)
                                                    </DropdownMenuItem>
                                                )}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    )}

                                    {/* Reagendar — só pra agendados */}
                                    {(p.status === 'queued') && p.scheduled_at?.indexOf('9999') === -1 && (
                                        <Button
                                            size="sm" variant="ghost"
                                            onClick={() => setRescheduleTarget(p)}
                                            className="text-amber-700 hover:bg-amber-50"
                                            title="Escolhe um novo horário do horizonte"
                                        >
                                            <Calendar className="w-3 h-3 mr-1" /> Reagendar
                                        </Button>
                                    )}

                                    {p.status !== 'published' && (
                                        <Button
                                            size="sm" variant="ghost"
                                            onClick={() => { if (confirm('Cancelar post?')) can.mutate(p.id); }}
                                            className="text-red-600 hover:bg-red-50"
                                        >
                                            <XCircle className="w-3 h-3 mr-1" /> Cancelar
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Paginação — mostra últimos N + botão pra carregar mais */}
            {!isLoading && currentList.length > 0 && (
                <div className="flex flex-col items-center gap-2 pt-2">
                    <div className="text-xs text-gray-500">
                        Mostrando {visibleList.length} de {currentList.length}
                    </div>
                    {hasMore && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setVisibleCount((c) => c + 12)}
                        >
                            Carregar mais 12
                        </Button>
                    )}
                </div>
            )}

            {/* Modal de reagendar com grid dia × horário + swap */}
            <RescheduleModal
                open={!!rescheduleTarget}
                onOpenChange={(o) => { if (!o) setRescheduleTarget(null); }}
                post={rescheduleTarget}
                pageConfigId={pageConfigId}
            />
        </div>
    );
}


export default function PageConfigDetail() {
    const { id } = useParams<{ id: string }>();
    const cfgId = Number(id);
    const [searchParams] = useSearchParams();
    // Deep-link: /configs/:id?tab=posts abre direto na aba de posts (ex: vindo
    // da lista de páginas da categoria).
    const initialTab = searchParams.get('tab') === 'posts' ? 'posts' : 'feeds';
    const qc = useQueryClient();
    const [linkOpen, setLinkOpen] = useState(false);
    const [syncOpen, setSyncOpen] = useState(false);

    const { data: cfg } = useQuery({
        queryKey: ['social-publisher', 'page-config', cfgId],
        queryFn: () => getPageConfig(cfgId),
        enabled: !!cfgId,
    });
    const { data: linkedFeeds = [] } = useQuery({
        queryKey: ['social-publisher', 'feeds-for-page', cfgId],
        queryFn: () => listFeedsForPage(cfgId),
        enabled: !!cfgId,
    });

    if (!cfgId || !cfg) {
        return <div className="p-6 text-gray-400 text-sm">Carregando...</div>;
    }

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center gap-3">
                <Link to="/meta/social-publisher" className="text-gray-400 hover:text-gray-600">
                    <ArrowLeft className="w-5 h-5" />
                </Link>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    {cfg.page_picture_url ? (
                        <img
                            src={cfg.page_picture_url}
                            alt=""
                            className="w-12 h-12 rounded-full object-cover border-2 border-purple-100"
                        />
                    ) : (
                        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-300">
                            <Facebook className="w-5 h-5" />
                        </div>
                    )}
                    <div className="min-w-0">
                        <h1 className="text-xl font-bold text-gray-800 truncate">
                            {cfg.page_name || `page_pk #${cfg.page_pk}`}
                        </h1>
                        <p className="text-xs text-gray-500">
                            {cfg.persona_name && <span className="text-purple-600">🎭 {cfg.persona_name} · </span>}
                            Posts: {(cfg.post_schedule_times || []).join(', ') || '—'} ·
                            Stories: {(cfg.story_schedule_times || []).join(', ') || '—'}
                        </p>
                    </div>
                </div>
            </div>

            <Tabs defaultValue={initialTab}>
                <TabsList>
                    <TabsTrigger value="feeds">
                        <Rss className="w-3.5 h-3.5 mr-1" /> Feeds RSS ({linkedFeeds.length})
                    </TabsTrigger>
                    <TabsTrigger value="posts">
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Posts
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="feeds" className="space-y-3 pt-4">
                    <div className="flex justify-end gap-2">
                        <Button
                            onClick={() => setSyncOpen(true)}
                            variant="outline"
                            className="border-purple-300 text-purple-700 hover:bg-purple-50"
                            disabled={linkedFeeds.length === 0}
                            title={linkedFeeds.length === 0 ? 'Vincule feeds primeiro' : 'Gera posts pra essa página com base nos items dos feeds vinculados'}
                        >
                            <Sparkles className="w-4 h-4 mr-2" /> Sincronizar fila
                        </Button>
                        <Button onClick={() => setLinkOpen(true)} className="bg-purple-600 hover:bg-purple-700">
                            <Plus className="w-4 h-4 mr-2" /> Vincular feeds
                        </Button>
                    </div>
                    {linkedFeeds.length === 0 ? (
                        <div className="bg-white border rounded-xl p-12 text-center">
                            <Rss className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                            <p className="text-sm text-gray-500 mb-3">
                                Nenhum feed vinculado a esta página
                            </p>
                            <Button size="sm" onClick={() => setLinkOpen(true)}>
                                <Plus className="w-4 h-4 mr-2" /> Vincular agora
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {linkedFeeds.map((f) => (
                                <div key={f.id} className="bg-white border rounded-xl p-4 flex items-center justify-between">
                                    <div className="min-w-0">
                                        <div className="text-sm font-semibold text-gray-800 truncate">
                                            {f.name || f.feed_url}
                                        </div>
                                        <a
                                            href={f.feed_url} target="_blank" rel="noreferrer"
                                            className="text-[11px] text-purple-600 hover:underline inline-flex items-center gap-0.5"
                                        >
                                            {f.feed_url} <ExternalLink className="w-2.5 h-2.5" />
                                        </a>
                                        <div className="text-[10px] text-gray-400 mt-1">
                                            {f.last_polled_at
                                                ? `Puxado ${fmtRelative(f.last_polled_at)}`
                                                : 'Aguardando primeiro pull'}
                                            {f.last_error && (
                                                <span className="text-red-600 ml-2 inline-flex items-center gap-1">
                                                    <AlertCircle className="w-3 h-3" /> {f.last_error}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        <Button
                                            size="sm" variant="ghost"
                                            onClick={async () => {
                                                const next = linkedFeeds.filter((x) => x.id !== f.id).map((x) => x.id);
                                                await setFeedsForPage(cfgId, next);
                                                qc.invalidateQueries({ queryKey: ['social-publisher', 'feeds-for-page', cfgId] });
                                            }}
                                            className="text-red-600 hover:bg-red-50"
                                            title="Desvincular"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="posts" className="pt-4">
                    <PostsTab pageConfigId={cfgId} />
                </TabsContent>
            </Tabs>

            <LinkFeedsDialog
                open={linkOpen}
                onOpenChange={setLinkOpen}
                pageConfigId={cfgId}
                currentFeedIds={linkedFeeds.map((f) => f.id)}
                onSaved={() => {
                    setLinkOpen(false);
                    qc.invalidateQueries({ queryKey: ['social-publisher', 'feeds-for-page', cfgId] });
                }}
            />

            <SyncModal
                open={syncOpen}
                onOpenChange={setSyncOpen}
                pageConfigId={cfgId}
            />
        </div>
    );
}
